import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, FileText, Search, Sparkles, X } from 'lucide-react'
import { useVocabularyMap } from '../hooks/useProgress'
import type { TopicCluster } from '../types'

// ── Topic label formatter ─────────────────────────────────────────────────────

function labelTopic(topic: string): string {
  return topic
    .split(/[\s_-]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

// Topics with some words started but coverage below this threshold get a
// "needs attention" left-border indicator (--warning family, at most one type).
const NEEDS_ATTENTION_THRESHOLD = 15

// ── Skeleton ──────────────────────────────────────────────────────────────────

function VocabularyMapSkeleton() {
  return (
    <div
      className="grid gap-2.5"
      style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}
      aria-hidden="true"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="animate-pulse rounded-2xl"
          style={{ minHeight: 80, background: 'var(--border)' }}
        />
      ))}
    </div>
  )
}

// ── Empty state ───────────────────────────────────────────────────────────────
// Dashed border is valid here — this is a true placeholder slot (Wave 4 §4).

function VocabularyMapEmpty({ onImport }: { onImport: () => void }) {
  return (
    <div
      className="flex flex-col items-center gap-4 py-8 px-5 rounded-2xl text-center"
      style={{ background: 'var(--bg-secondary)', border: '1.5px dashed var(--border)' }}
    >
      <div
        className="flex items-center justify-center w-12 h-12 rounded-2xl"
        style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
      >
        <Sparkles size={22} strokeWidth={1.5} />
      </div>
      <div>
        <p className="font-semibold text-sm mb-1" style={{ color: 'var(--text-primary)' }}>
          Your vocabulary map is empty
        </p>
        <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
          Import your first text to see topic coverage visualised here.
        </p>
      </div>
      <button
        onClick={onImport}
        className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full transition-opacity hover:opacity-80"
        style={{ background: 'var(--accent-fill)', color: 'var(--text-on-accent)' }}
      >
        <FileText size={13} /> Import a text
      </button>
    </div>
  )
}

// ── Suggested topic chips ─────────────────────────────────────────────────────

function SuggestedChips({ topics }: { topics: string[] }) {
  if (topics.length === 0) return null
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
        Focus next:
      </span>
      {topics.map((t) => (
        <span
          key={t}
          className="text-xs font-medium px-2.5 py-1 rounded-full"
          style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
        >
          {labelTopic(t)}
        </span>
      ))}
    </div>
  )
}

// ── Tile ──────────────────────────────────────────────────────────────────────
// Replaces the bubble. Coverage magnitude is encoded by the accent bar fill
// length at the bottom — a continuous deck-level aggregate bar (not ticked,
// per Wave 3 §1 amendment: ticks apply only to per-word mastery indicators).

interface TileProps {
  cluster: TopicCluster
  selected: boolean
  onClick: () => void
}

function Tile({ cluster, selected, onClick }: TileProps) {
  const { topic, total_available, known, coverage_pct } = cluster
  const isEmpty = known === 0
  const label = labelTopic(topic)
  const barWidth = isEmpty ? 0 : Math.max(coverage_pct, 2)
  const needsAttention = !isEmpty && coverage_pct < NEEDS_ATTENTION_THRESHOLD

  // Border logic: selected → full accent ring; needs-attention → warning left accent;
  // otherwise → standard border. Warning is the one and only --warning-family touch.
  const borderStyle: React.CSSProperties = selected
    ? { border: '2px solid var(--accent)', boxShadow: '0 0 0 3px var(--accent-soft)' }
    : needsAttention
      ? { border: '1.5px solid var(--border)', borderLeft: '3px solid var(--warning)' }
      : { border: '1.5px solid var(--border)' }

  return (
    <button
      onClick={onClick}
      className="flex flex-col rounded-2xl transition-all duration-200 focus:outline-none text-left cursor-pointer"
      style={{
        padding: '12px 12px 10px',
        minHeight: 80,
        background: isEmpty ? 'var(--bg-secondary)' : 'var(--bg-surface)',
        ...borderStyle,
      }}
      aria-pressed={selected}
      aria-label={`${label}: ${isEmpty ? 'not started' : `${Math.round(coverage_pct)}% coverage, ${known} of ${total_available} words`}`}
    >
      {/* Topic name + stats — flex-1 pushes bar to bottom */}
      <div className="flex-1">
        <span
          className="font-semibold text-sm leading-snug block"
          style={{
            color: isEmpty ? 'var(--text-secondary)' : 'var(--text-primary)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical' as const,
            overflow: 'hidden',
          }}
        >
          {label}
        </span>
        <div className="flex items-baseline gap-1.5 mt-0.5">
          <span
            className="text-xs font-bold tabular-nums"
            style={{ color: isEmpty ? 'var(--text-secondary)' : 'var(--accent)' }}
          >
            {isEmpty ? '—' : `${Math.round(coverage_pct)}%`}
          </span>
          {total_available > 0 && (
            <span className="text-xs tabular-nums" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>
              {known} / {total_available}
            </span>
          )}
        </div>
      </div>

      {/* Coverage bar — always rendered, zero-width when not started */}
      <div
        className="w-full h-1 rounded-full overflow-hidden mt-3 flex-shrink-0"
        style={{ background: 'var(--border)' }}
        role="progressbar"
        aria-valuenow={coverage_pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label} coverage`}
      >
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${barWidth}%`, background: 'var(--accent)' }}
        />
      </div>
    </button>
  )
}

// ── Detail panel ──────────────────────────────────────────────────────────────

interface DetailPanelProps {
  cluster: TopicCluster
  onClose: () => void
}

function DetailPanel({ cluster, onClose }: DetailPanelProps) {
  const navigate = useNavigate()
  const { topic, total_available, known, in_progress, coverage_pct } = cluster
  const label = labelTopic(topic)
  const knownWidth = total_available > 0 ? Math.min((known / total_available) * 100, 100) : 0
  const inProgressWidth = total_available > 0
    ? Math.min((in_progress / total_available) * 100, 100 - knownWidth)
    : 0

  return (
    <div
      className="w-full rounded-2xl p-4 flex flex-col gap-3"
      style={{ background: 'var(--bg-surface)', border: '1.5px solid var(--border)' }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-bold text-base" style={{ color: 'var(--text-primary)' }}>
          {label}
        </h3>
        <button
          onClick={onClose}
          className="flex items-center justify-center w-6 h-6 rounded-full transition-opacity hover:opacity-70 flex-shrink-0"
          style={{ color: 'var(--text-secondary)', background: 'var(--bg-secondary)' }}
          aria-label="Close detail panel"
        >
          <X size={12} strokeWidth={2.5} />
        </button>
      </div>

      {/* Coverage bar — two-layer: in-progress (dimmed accent), known (full accent) */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
            Coverage
          </span>
          <span className="text-xs font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>
            {known === 0 ? 'Not started' : `${coverage_pct}%`}
          </span>
        </div>
        <div
          className="w-full h-2.5 rounded-full overflow-hidden relative"
          style={{ background: 'var(--border)' }}
          role="progressbar"
          aria-valuenow={coverage_pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          {/* In-progress layer: accent at reduced opacity */}
          {inProgressWidth > 0 && (
            <div
              className="absolute inset-y-0 left-0 rounded-full"
              style={{
                width: `${knownWidth + inProgressWidth}%`,
                background: 'var(--accent)',
                opacity: 0.3,
              }}
            />
          )}
          {/* Known layer */}
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
            style={{ width: `${knownWidth}%`, background: 'var(--accent)' }}
          />
        </div>
      </div>

      {/* Stats row — neutral colors, no semantic success/warning as categories */}
      <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--text-secondary)' }}>
        <span>
          <strong style={{ color: 'var(--accent)' }}>{known.toLocaleString()}</strong> known
        </span>
        <span>
          <strong style={{ color: 'var(--text-primary)' }}>{in_progress.toLocaleString()}</strong> in progress
        </span>
        <span>
          <strong style={{ color: 'var(--text-primary)' }}>{total_available.toLocaleString()}</strong> available
        </span>
      </div>

      {/* CTAs */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => navigate('/text-import')}
          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full transition-opacity hover:opacity-80"
          style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
        >
          <FileText size={12} />
          Import text about {label.toLowerCase()}
        </button>
        <button
          onClick={() => navigate(`/explore?q=${encodeURIComponent(topic)}`)}
          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full transition-opacity hover:opacity-80"
          style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
        >
          <Search size={12} />
          Explore {label.toLowerCase()} decks
          <ArrowRight size={11} />
        </button>
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function VocabularyMap() {
  const navigate = useNavigate()
  const { data, isLoading } = useVocabularyMap()
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null)

  if (isLoading) return <VocabularyMapSkeleton />

  const clusters = data?.clusters ?? []
  const suggested = data?.suggested_topics ?? []

  const hasAnyWords = clusters.some((c) => c.total_available > 0)
  if (!hasAnyWords) {
    return <VocabularyMapEmpty onImport={() => navigate('/text-import')} />
  }

  // Keep "Other" at the end, sort the rest: started → by coverage desc, not-started → alphabetical
  const mainClusters = clusters.filter((c) => c.topic !== 'Other')
  const otherCluster = clusters.find((c) => c.topic === 'Other')

  const sorted = [...mainClusters].sort((a, b) => {
    const aStarted = a.known > 0
    const bStarted = b.known > 0
    if (aStarted !== bStarted) return aStarted ? -1 : 1
    if (aStarted) return b.coverage_pct - a.coverage_pct
    return a.topic.localeCompare(b.topic)
  })
  const allVisible = otherCluster ? [...sorted, otherCluster] : sorted

  const selected = allVisible.find((c) => c.topic === selectedTopic) ?? null

  function handleTileClick(topic: string) {
    setSelectedTopic((prev) => (prev === topic ? null : topic))
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      {suggested.length > 0 && <SuggestedChips topics={suggested} />}

      {/* Ordered grid — magnitude carried by the per-tile accent bar fill length */}
      <div
        className="grid gap-2.5"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}
        role="list"
        aria-label="Vocabulary coverage by topic"
      >
        {allVisible.map((cluster) => (
          <div key={cluster.topic} role="listitem">
            <Tile
              cluster={cluster}
              selected={selectedTopic === cluster.topic}
              onClick={() => handleTileClick(cluster.topic)}
            />
          </div>
        ))}
      </div>

      {/* Detail panel — expands below when a tile is selected */}
      {selected && (
        <DetailPanel
          cluster={selected}
          onClose={() => setSelectedTopic(null)}
        />
      )}

      {/* Legend — uses tile swatches, not color circles */}
      <div className="flex items-center gap-4 flex-wrap" aria-hidden="true">
        <div className="flex items-center gap-1.5">
          <div
            style={{
              width: 28, height: 16, borderRadius: 4,
              background: 'var(--bg-secondary)', border: '1.5px solid var(--border)',
            }}
          />
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Not started</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div
            style={{
              width: 28, height: 16, borderRadius: 4,
              background: 'var(--bg-surface)', border: '1.5px solid var(--border)',
              position: 'relative', overflow: 'hidden',
            }}
          >
            <div
              style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, height: 3,
                background: 'var(--accent)',
              }}
            />
          </div>
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Coverage (bar = %)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div
            style={{
              width: 28, height: 16, borderRadius: 4,
              background: 'var(--bg-surface)',
              border: '1.5px solid var(--border)',
              borderLeft: '3px solid var(--warning)',
            }}
          />
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Needs attention</span>
        </div>
      </div>
    </div>
  )
}
